import React, { Fragment } from 'react'
import { Grid, Typography, IconButton, Box, FormControl, InputLabel, Select, MenuItem, Link, makeStyles } from '@material-ui/core'
import { Edit, Delete, GetApp } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { useAlert, useBatch, useChangeHandler, useRemove, useUpdate } from '../reducer'
import { mapByIds, useFetch, useAuth } from '../context/useFetch'
import { getFileExtensionIcon } from '../common/get_file_extension_icon'
import { DropzoneDocs } from '../utils/DropzoneDocs'
import { API_HOST } from '../../src/constants'
import i18n from 'i18next'
import HumanFileSize from '../common/human_file_size'
import { Trans } from 'react-i18next'

const useStyles = makeStyles((theme) => ({
  document: {
    marginBottom: theme.spacing(4),
    '& .ge--MuiGrid-container': {
      paddingTop: theme.spacing(2),
      '&:nth-of-type(1)': {
        paddingTop: theme.spacing(0)
      },
      '&:not(:last-child)': {
        paddingBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.text.disabled}`
      },
      '& .fileIcon': {
        display: 'grid',
        gridTemplateColumns: '24px 1fr',
        alignItems: 'center',
        columnGap: theme.spacing(1),
        '& img': {
          maxWidth: '24px',
          maxHeight: '24px'
        }
      }
    }
  }
}))

const Documents = ({ slug = 'other' }) => useFetch(
  ({ fetch }) => ({
    documentTypes: fetch('document_types').then(mapByIds),
    documents: fetch('profile/documents').then(({ data, included }) => ({
      data: mapByIds(data),
      included
    }))
  }),
  ({ documentTypes }) => {
    const documentIds = useSelector((state) => Object.keys(state.documents.data))
    const documentType = Object.values(documentTypes).find((document) => document.slug === slug)

    const classes = useStyles()

    return (
      <Fragment>
        <Typography variant="h2" component="h1">
          {i18n.t('profile.candidat.documents.title')}
        </Typography>

        <Grid className="profileSection">
          <Grid className={classes.document}>
            {documentIds.map((id) => <SingleDocument id={id} />)}
          </Grid>

          <UploadDocuments documentType={documentType} />
        </Grid>
      </Fragment>
    )
  }
)

export const UploadDocuments = ({ documentType, callback }) => {
  const auth = useAuth()
  const alert = useAlert()
  const update = useUpdate()

  const reloadDocuments = async () => {
    await auth.fetch('profile/documents').then((documents) => {
      update(['documents'], {
        data: mapByIds(documents.data),
        included: documents.included
      })
      alert('notice', <Trans i18nKey="alerts.saved_data" />)
      callback && callback()
    })
  }

  const failureHandler = () => {
    alert('error', <Trans i18nKey="alerts.saved_error" />)
  }

  return (
    <DropzoneDocs
      likeLabel={i18n.t('profile.candidat.documents.add')}
      postUrl={`//${API_HOST()}/api/profile/documents`}
      dictDefaultMessage={i18n.t('registration.candidat.step0.dropzone_text')}
      acceptedFiles='application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      paramName='document[file]'
      params={{ 'document[document_type_id]': documentType.id }}
      eventHandlers={{
        success: reloadDocuments,
        failure: failureHandler
      }}
    />
  )
}

export default Documents

const SingleDocument = ({ id }) => {
  const document = useSelector((state) => state.documents.data[id])
  const documentTypes = useSelector((state) => state.documentTypes)

  const handler = useChangeHandler(document, 'documents', id)

  const update = useUpdate()
  const batch = useBatch()
  const alert = useAlert()
  const remove = useRemove()

  const auth = useAuth()

  const changeTypeHandler = ({ target: { value } }) => {
    batch(['documents', 'data', id], {
      editMode: false,
      document_type: {
        ...document.document_type,
        id: value
      }
    })

    auth
      .fetch(`profile/documents/${id}`, { method: 'PATCH', body: { document: { document_type_id: value } } })
      .then(() => alert('notice', 'Type enregistré avec succès'))
      .catch(() => alert('error', 'Une erreur s\'est produite'))
  }

  const downloadHandler = (documentId) => (e) => {
    e.preventDefault()

    auth.fetch(`profile/documents/${documentId}/download`).then(({ url }) => {
      window.open(url)
    })
  }

  const deleteHandler = (id) => () => {
    confirm(i18n.t('profile.candidat.documents.confirm_delete')) && auth.deleteFetch(`profile/documents/${id}`).then(() => {
      remove(['documents', 'data', id])
    })
  }

  return (
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid>
        <Link href="#" onClick={downloadHandler(document.hashid)} variant="h4" className="fileIcon" target="_blank">
          {getFileExtensionIcon(document.file_blob.filename)}
          {document.file_blob.filename}
        </Link>

        <Box mt={1}>
          {!document.editMode && (
            <Typography variant="body2" component="p" color="textSecondary">
              {HumanFileSize(document.file_blob.byte_size)}
              &nbsp;—&nbsp;
              {documentTypes[document.document_type.id].label}
            </Typography>
          )}

          {document.editMode && (
            <FormControl variant="filled">
              <InputLabel id="document-label">
                {i18n.t('profile.candidat.documents.change')}
              </InputLabel>
              <Select
                id="document-type"
                labelId="document-label"
                style={{
                  width: '270px'
                }}
                value={document.document_type.id}
                {...handler.default('document_type', 'id')}
                onChange={changeTypeHandler}
              >
                {Object.values(documentTypes).map((type, i) =>
                  <MenuItem value={type.id} key={i}>{type.label}</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </Box>
      </Grid>

      <Grid>
        <IconButton aria-label="edit" color="secondary" onClick={() => update(['documents', 'data', id, 'editMode'], !document.editMode)}>
          <Edit />
        </IconButton>
        <IconButton aria-label="download" onClick={downloadHandler(document.hashid)}>
          <GetApp />
        </IconButton>
        <IconButton aria-label="delete" onClick={deleteHandler(id)}>
          <Delete />
        </IconButton>
      </Grid>
    </Grid>
  )
}
